import { ProductCategory } from "@utils/constants";
import { useCustomSearchData } from "@redux/slices/searchResultsCustomization";
import { useTranslation } from "react-i18next";
import { useUnifiedProductPagesSlice } from "@redux/slices/unifiedProductPages";
import {
  SearchFilterTypeEnum,
  SearchFilterTypeEnumLowercase,
} from "@components/atoms/Filters/utils";
import { StoneType } from "@scenes/bulk-request/Utils";
import { getSortingFiltersOptions } from "@utils/product/sortingFilters";

export const useSortingData = (
  productType: ProductCategory,
  sectionName: SearchFilterTypeEnum
) => {
  const customSearchData = useCustomSearchData();

  const getSortingData = () => {
    const dataObject = customSearchData[productType];

    if (!dataObject || typeof dataObject !== "object") {
      return null;
    }

    const sortingSection = dataObject.sorting;
    switch (sectionName) {
      case SearchFilterTypeEnum.SINGLE_STONES:
        if (
          sortingSection &&
          sortingSection.options &&
          typeof sortingSection.options === "object"
        ) {
          return Object.values(sortingSection.options);
        }
        break;
      case SearchFilterTypeEnum.PAIRS:
        if (
          sortingSection &&
          sortingSection?.["sub-sections"]?.[
            SearchFilterTypeEnumLowercase.PAIRS
          ] &&
          typeof sortingSection?.["sub-sections"]?.[
            SearchFilterTypeEnumLowercase.PAIRS
          ]?.options === "object"
        ) {
          return Object.values(
            sortingSection?.["sub-sections"]?.[
              SearchFilterTypeEnumLowercase.PAIRS
            ]?.options
          );
        }
        break;
      case SearchFilterTypeEnum.PARCEL:
        if (
          sortingSection &&
          sortingSection?.["sub-sections"]?.parcels &&
          typeof sortingSection?.["sub-sections"]?.parcels?.options === "object"
        ) {
          return Object.values(
            sortingSection?.["sub-sections"]?.parcels?.options
          );
        }
        break;
      default:
        if (
          sortingSection &&
          sortingSection.options &&
          typeof sortingSection.options === "object"
        ) {
          return Object.values(sortingSection.options);
        }
        break;
    }

    return null;
  };

  return getSortingData();
};

interface SortingOption {
  name: string;
  label: string;
  priceModes: number[];
  default_order?: string;
  order?: number;
  is_sortable: boolean;
  deafult_width: string;
  response_key: string;
  alignment?: string;
}

export const useGenerateSortingOptions = (
  productType: ProductCategory,
  priceMode: number,
  sectionName: SearchFilterTypeEnum = SearchFilterTypeEnum.SINGLE_STONES
) => {
  const { t } = useTranslation();
  const sortingData = useSortingData(productType, sectionName);
  const INACTIVE_OPTIONS = getSortingFiltersOptions(t, priceMode, productType);

  const generateSortingOptions = (): SortingOption[] => {
    if (!sortingData || !Array.isArray(sortingData)) {
      return INACTIVE_OPTIONS;
    }

    const inactiveOptionsMap = new Map(
      INACTIVE_OPTIONS.map((option) => [option.name, option])
    );

    const sortingOptions = sortingData
      .map((item: any) => {
        const inactiveOption = inactiveOptionsMap.get(item.response_key);
        return {
          name: item.response_key,
          label: t(item.label),
          priceModes:
            item.price_modes ||
            (inactiveOption ? inactiveOption.priceModes : []),
          default_order: item.default_order ?? "desc",
          order: item.order,
          is_sortable: item.is_sortable,
          deafult_width: item.deafult_width,
          response_key: item.response_key,
          additional_attributes: item.additional_attributes,
        };
      })
      .filter((option: any) => option.priceModes.includes(priceMode));

    sortingOptions.sort((a, b) => (a.order || 0) - (b.order || 0));
    return sortingOptions;
  };

  return generateSortingOptions();
};

const getBulkListData = (
  unifiedProductPagesSlice: any,
  productType: string,
  selectedStoneType?: StoneType,
  key: "cps_view" | "item_details" | "price_details",
  fromBulkList?: boolean,
  isMobile?: boolean
) => {
  const base = unifiedProductPagesSlice?.unifiedProductData?.[productType];
  const screenKey = fromBulkList ? "bulkList" : "requests";
  if (selectedStoneType === StoneType.PAIR) {
    return base?.pair?.[screenKey]?.list_view?.[key];
  }

  if (selectedStoneType === StoneType.PARCELS) {
    if (isMobile) {
      return base?.parcels?.mobile_parcels?.[screenKey]?.list_view?.[key];
    }
    return base?.parcels?.[screenKey]?.list_view?.[key];
  }

  return base?.[screenKey]?.list_view?.[key];
};

export const useItemDetailsCardSubSections = (
  productType: ProductCategory,
  priceMode: number,
  sectionName: string | null = "Single Stones",
  fromBulkList: boolean = false,
  selectedStoneType?: StoneType,
  fromRequestDetail?: boolean,
  isMobile?: boolean
) => {
  const customSearchData = useCustomSearchData();
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();

  const bulkCPSViewDataOptions = getBulkListData(
    unifiedProductPagesSlice,
    productType,
    selectedStoneType,
    "cps_view",
    fromBulkList,
    isMobile
  );

  const bulkItemDetailsDataOptions = getBulkListData(
    unifiedProductPagesSlice,
    productType,
    selectedStoneType,
    "item_details",
    fromBulkList,
    isMobile
  );

  const bulkPriceDetailsDataOptions = getBulkListData(
    unifiedProductPagesSlice,
    productType,
    selectedStoneType,
    "price_details",
    fromBulkList,
    isMobile
  );

  const bulkShowStockNum =
    unifiedProductPagesSlice?.unifiedProductData?.[productType]?.bulkList
      ?.list_view?.show_stock_num;

  const getItemDetailsCardSubSections = () => {
    const dataObject = customSearchData?.[productType];
    if (!dataObject?.list_view?.["sub-sections"]) return null;
    const listViewSectionName =
      sectionName === SearchFilterTypeEnum.PARCEL
        ? "itemDetailsCardParcels"
        : sectionName === SearchFilterTypeEnum.PAIRS
        ? "itemDetailsCardPair"
        : "itemDetailsCard";
    const itemDetailsCard = dataObject.list_view["sub-sections"]?.[
      listViewSectionName
    ]
      ? dataObject.list_view["sub-sections"]?.[listViewSectionName]?.[
          "sub-sections"
        ]
      : dataObject.list_view["sub-sections"]?.itemDetailsCard?.["sub-sections"];
    if (!itemDetailsCard && !fromBulkList) return null;

    const extractOptions = (section: any) =>
      Object.values(section?.options || {})
        .filter((option: any) => option.price_modes.includes(priceMode))
        .sort((a: any, b: any) => parseFloat(a.order) - parseFloat(b.order));

    const cpsViewDataOptions = extractOptions(
      fromBulkList || fromRequestDetail
        ? bulkCPSViewDataOptions
        : itemDetailsCard?.cps_view
    );
    const itemDetailsDataOptions = extractOptions(
      fromBulkList || fromRequestDetail
        ? bulkItemDetailsDataOptions
        : itemDetailsCard?.item_details
    );
    const priceDetailsDataOptions = extractOptions(
      fromBulkList || fromRequestDetail
        ? bulkPriceDetailsDataOptions
        : itemDetailsCard?.price_details
    );
    const isEnabledForPriceMode = (
      config: any,
      userPriceMode: number
    ): boolean => {
      if (!config) {
        return true;
      } else if (config?.price_modes?.length === 0) {
        return false;
      } else if (!config?.price_modes) {
        return true;
      }
      return config?.price_modes?.includes(userPriceMode);
    };
    const showStockNum = fromBulkList
      ? isEnabledForPriceMode(bulkShowStockNum, priceMode)
      : isEnabledForPriceMode(itemDetailsCard?.show_stock_num, priceMode);
    const defaultRows =
      fromBulkList || fromRequestDetail
        ? bulkItemDetailsDataOptions?.default_rows || null
        : itemDetailsCard?.item_details?.default_rows || null;
    const defaultRowsWeb = bulkItemDetailsDataOptions?.default_rows_web || null;

    return {
      cpsViewDataOptions,
      itemDetailsDataOptions,
      priceDetailsDataOptions,
      defaultRows,
      defaultRowsWeb,
      showStockNum,
      bulkPriceDetailsDataOptions,
    };
  };

  return getItemDetailsCardSubSections();
};

export const useTabletListViewSubSections = (
  productType: ProductCategory,
  fromBulkList: boolean,
  fromRequestDetail: boolean,
  sectionName: SearchFilterTypeEnum = SearchFilterTypeEnum.SINGLE_STONES,
  selectedStoneType?: StoneType
) => {
  const customSearchData = useCustomSearchData();
  const unifiedProductPagesSlice = useUnifiedProductPagesSlice();
  const getTabletListViewSubSections = () => {
    const dataObject = customSearchData[productType];
    if (!dataObject || typeof dataObject !== "object") {
      return null;
    }

    const listViewSection = fromBulkList
      ? selectedStoneType === StoneType.PAIR
        ? unifiedProductPagesSlice?.unifiedProductData?.[productType]?.pair
            ?.bulkList?.bulk_list_tablet
        : selectedStoneType === StoneType.PARCELS
        ? unifiedProductPagesSlice?.unifiedProductData?.[productType]?.parcels
            ?.bulkList?.bulk_list_tablet
        : unifiedProductPagesSlice?.unifiedProductData?.[productType]?.bulkList
            ?.bulk_list_tablet
      : fromRequestDetail
      ? selectedStoneType === StoneType.PAIR
        ? unifiedProductPagesSlice?.unifiedProductData?.[productType]?.pair
            ?.requests?.bulk_list_tablet
        : selectedStoneType === StoneType.PARCELS
        ? unifiedProductPagesSlice?.unifiedProductData?.[productType]?.parcels
            ?.requests?.bulk_list_tablet
        : unifiedProductPagesSlice?.unifiedProductData?.[productType]?.requests
            ?.bulk_list_tablet
      : dataObject?.list_view_tablet;
    if (
      listViewSection &&
      listViewSection["sub-sections"] &&
      typeof listViewSection["sub-sections"] === "object"
    ) {
      const listViewColumnName =
        sectionName === SearchFilterTypeEnum.PARCEL
          ? "list_view_column_parcels"
          : sectionName === SearchFilterTypeEnum.PAIRS
          ? "list_view_column_pair"
          : "list_view_column";
      const itemDetailsCard =
        listViewSection["sub-sections"]?.[listViewColumnName] ??
        listViewSection["sub-sections"]?.list_view_column;

      if (
        itemDetailsCard &&
        itemDetailsCard.options &&
        typeof itemDetailsCard.options === "object"
      ) {
        return itemDetailsCard.options;
      }
    }
    return null;
  };

  return getTabletListViewSubSections();
};

export const useSortedTabletListData = (
  productType: ProductCategory,
  priceMode: number,
  sectionName: string | null = "Single Stones",
  fromBulkList?: boolean,
  fromRequestDetail?: boolean,
  selectedStoneType?: StoneType
) => {
  const { t } = useTranslation();
  const listData = useTabletListViewSubSections(
    productType,
    fromBulkList ?? false,
    fromRequestDetail ?? false,
    sectionName,
    selectedStoneType
  );
  const INACTIVE_OPTIONS = getSortingFiltersOptions(t, priceMode, productType);
  const sortedTabletListData = (): SortingOption[] => {
    if (!listData || typeof listData !== "object") {
      return INACTIVE_OPTIONS;
    }

    const inactiveOptionsMap = new Map(
      INACTIVE_OPTIONS.map((option) => [option.name, option])
    );
    const listDataOptions = Object.values(listData)
      .map((item: any) => {
        const inactiveOption = inactiveOptionsMap.get(item.response_key);
        return {
          name: item.name,
          label: t(item.label),
          priceModes:
            item.price_modes ||
            (inactiveOption ? inactiveOption.priceModes : []),
          default_sort: item.default_sort ?? "ASC",
          order: item.order,
          is_sortable: item.is_sortable,
          deafult_width: item.deafult_width,
          response_key: item.response_key,
          alignment: item?.alignment ?? "flex-start",
          additional_attributes: item.additional_attributes,
          enable_tooltip: item?.enable_tooltip ?? false,
          ...(item?.tooltip_text && {
            tooltip_text: t(item.tooltip_text),
          }),
          ...(item?.popover_data_response_key && {
            popover_data_response_key: item.popover_data_response_key,
          }),
          ...(item?.show_popover_data && {
            show_popover_data: item.show_popover_data,
          }),
          ...(item?.sort_key && {
            sort_key: item.sort_key,
          }),
        };
      })
      .filter((option) => option.priceModes.includes(priceMode));
    listDataOptions.sort((a, b) => (a.order || 0) - (b.order || 0));
    return listDataOptions;
  };

  return sortedTabletListData();
};

export const useDisplayImage = (productType: ProductCategory) => {
  const customSearchData = useCustomSearchData();

  const getDisplayImage = () => {
    const listViewData = customSearchData[productType];

    if (!listViewData) {
      return false;
    }

    if (
      listViewData?.list_view_tablet &&
      "displayImage" in listViewData?.list_view_tablet
    ) {
      return listViewData.list_view_tablet.displayImage;
    }

    return false;
  };

  return getDisplayImage();
};
